import { tr } from '@/lib/i18n-core';
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, FolderOpen, HandCoins, MoreHorizontal, Printer, Receipt, RefreshCw, type LucideIcon } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { toIsoDate } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { LinkChips, type LinkChip } from '@/components/LinkChips';
import { ActionMenu, ActionMenuItem } from '@/components/ui/action-menu';
import { Alert, Button, PageSkeleton } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import { VitalsDialog } from '@/components/VitalsDialog';
import { CertificatesPanel } from './CertificatesPanel';
import { AttachmentsPanel, CareTeamPanel } from './VisitExtras';
import { AllergiesPanel, ChiefComplaintBar, ClaimFlagsPanel, CloseConsultationButton, CurrentMedicinesPanel, ProblemListPanel, ReviewOfSystems } from './EmrCorePanels';
import { PreviousVisits } from '../patients/PreviousVisits';
import { AuthorisationProvider, useAuthorisation } from './AuthorisationContext';
import { LabOrderSection } from './emr/orders/LabOrderSection';
import { RadiologyOrderSection } from './emr/orders/RadiologyOrderSection';
import { ProcedureOrderSection } from './emr/orders/ProcedureOrderSection';
import { AuthorisationBanner } from './AuthorisationBanner';
import { bannerFor } from '../insurance/authorisation';
import { feeBannerFor, feeWatchOf } from './fee-gate';
import { useRealtime } from '@/lib/realtime';
import type { Encounter } from './emr/types';
import { EmrPatientBar } from './emr/EmrPatientBar';
import { EmrNav } from './emr/EmrNav';
import { EmrOverview } from './emr/EmrOverview';
import { NotePanel } from './emr/NoteFields';
import { VitalsSection } from './emr/VitalsSection';
import { nextVitalsAreRecheck } from './emr/vitals';
import { DiagnosisEditor } from './emr/DiagnosisEditor';
import { PrescriptionEditor } from './emr/PrescriptionEditor';
import { Addenda, FollowUpDate } from './emr/VisitWrapUp';
import { emrSections, examinationFields, formKeyOfSection, historyFields, planFields, sectionFromParams, sectionOfFormKey, type EmrNavId, type EmrSection, type EmrSectionId } from './emr/sections';
import { isClinicalFormCode, isEmrNotNeededKey } from '@his/shared';
import { newbornFactsOf, progressOf, requiredFormsApply, useEmrForms } from './emr/completion';
import { EmrProgressIndicator } from './emr/EmrProgress';
import { ClinicalFormSection } from './emr/ClinicalFormSection';
import { NotNeededToggle } from './emr/NotNeededToggle';

function SectionHeading({ section, description }: { section: EmrSection; description?: string }) {
  const Icon: LucideIcon = section.icon;
  return (
    <div className="flex items-center gap-3 px-1">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary" aria-hidden>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 leading-tight">
        <h2 className="text-base font-semibold first-letter:uppercase">{section.label}</h2>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

const outerHeight = (node: HTMLElement) => node.offsetHeight;
const visibleHeight = (node: HTMLElement) => node.clientHeight;

function useObservedHeight(node: HTMLElement | null, measure: (node: HTMLElement) => number): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setHeight(Math.round(measure(node))));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, measure]);
  return height;
}

function stickyLayoutVars(barHeight: number, scrollAreaHeight: number): CSSProperties {
  return {
    '--emr-bar-h': `${barHeight}px`,
    ...(scrollAreaHeight > 0 ? { '--emr-scroll-h': `${scrollAreaHeight}px` } : {}),
  } as CSSProperties;
}

export function ConsultationPage() {
  const { encounterId } = useParams<{ encounterId: string }>();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [recordingVitals, setRecordingVitals] = useState(false);
  const [barNode, setBarNode] = useState<HTMLDivElement | null>(null);
  const [scrollArea, setScrollArea] = useState<HTMLElement | null>(null);
  const barHeight = useObservedHeight(barNode, outerHeight);
  const scrollAreaHeight = useObservedHeight(scrollArea, visibleHeight);
  const attachPage = useCallback((node: HTMLDivElement | null) => setScrollArea(node?.closest<HTMLElement>('.app-scroll') ?? null), []);

  const encounter = useQuery({
    queryKey: ['encounter', encounterId],
    queryFn: () => get<Encounter>(`/encounters/${encounterId}`),
    enabled: Boolean(encounterId),
  });
  const emrForms = useEmrForms(encounter.data);

  const invoices = useQuery({
    queryKey: ['invoices', 'encounter', encounterId],
    queryFn: () => get<{ data: { id: string; number: string; status: string }[] }>(`/invoices${qs({ encounterId, limit: 5 })}`),
    enabled: Boolean(encounter.data) && can('invoice.read'),
  });

  const watchedVisit = feeWatchOf(encounter.data);
  useRealtime(watchedVisit ? [`queue:${watchedVisit.doctorId}`] : [], (_event, payload) => {
    if (watchedVisit && (payload as { appointmentId?: string } | null)?.appointmentId === watchedVisit.appointmentId) {
      void queryClient.invalidateQueries({ queryKey: ['encounter', encounterId] });
    }
  });

  if (encounter.isLoading) return <PageSkeleton />;
  if (!encounter.data) {
    return <Alert variant="destructive">{describeError(encounter.error) || tr('opd', 'that_consultation_could_not_be_opened')}</Alert>;
  }

  const e = encounter.data;
  const closed = e.status === 'closed';
  const sections = emrSections(e, { closed, forms: emrForms.forms });
  const active = sectionFromParams(params, sections);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['encounter', encounterId] });
  const progress = progressOf(e, emrForms.forms);
  const activeFormKey = formKeyOfSection(active);
  const activeForm = activeFormKey ? emrForms.forms.find((form) => form.key === activeFormKey) : undefined;

  const open = (id: EmrNavId) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('tab');
        if (id === 'overview') next.delete('section');
        else next.set('section', id);
        return next;
      },
      { replace: true },
    );
    scrollArea?.scrollTo({ top: 0 });
  };

  const related: LinkChip[] = [
    ...(e.appointmentId && can('appointment.read', 'appointment.manage')
      ? [{ to: `/appointments${qs({ view: 'list', date: toIsoDate(new Date(e.startedAt)), q: e.patient.mrn })}`, label: tr('opd', 'appointment') }]
      : []),
    ...(invoices.data?.data ?? [])
      .filter((invoice) => invoice.status !== 'cancelled')
      .map((invoice) => ({ to: `/billing/invoices/${invoice.id}`, label: `${tr('opd', 'invoice')} ${invoice.number}` })),
  ];

  const section = sections.find((candidate) => candidate.id === active) as EmrSection;
  const recordVitals = !closed && can('vitals.write') ? () => setRecordingVitals(true) : undefined;
  const orderSection = { scope: { encounterId: e.id }, patient: e.patient, readOnly: closed, fee: e, onChanged: refresh, onAttach: () => open('files') };

  const body: Record<EmrSectionId, () => ReactNode> = {
    overview: () => <EmrOverview encounter={e} sections={sections} onOpen={open} />,
    vitals: () => <VitalsSection encounter={e} onRecord={recordVitals} canMarkNotTaken={!closed && can('encounter.write')} />,
    complaint: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_complaint_desc')} />
        <ChiefComplaintBar encounter={e} readOnly={closed} />
        <NotePanel
          key="history"
          encounter={e}
          fields={historyFields()}
          readOnly={closed}
          withTemplates
          icon={section.icon}
          title={tr('opd', 'emr_history')}
        />
      </>
    ),
    ros: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_ros')} />
        <ReviewOfSystems encounter={e} readOnly={closed} />
      </>
    ),
    examination: () => (
      <NotePanel
        key="examination"
        encounter={e}
        fields={examinationFields()}
        readOnly={closed}
        icon={section.icon}
        title={section.label}
        description={tr('opd', 'emr_examination_desc')}
      />
    ),
    diagnosis: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_diagnosis')} />
        <DiagnosisEditor encounter={e} readOnly={closed} onSaved={refresh} departmentId={emrForms.ready ? emrForms.departmentId : undefined} />
        <ClaimFlagsPanel encounter={e} readOnly={closed} />
      </>
    ),
    plan: () => (
      <>
        <NotePanel
          key="plan"
          encounter={e}
          fields={planFields()}
          readOnly={closed}
          icon={section.icon}
          title={section.label}
          description={tr('opd', 'emr_plan_desc')}
        />
        {!closed && <FollowUpDate encounter={e} />}
      </>
    ),
    prescription: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_prescription')} />
        <PrescriptionEditor encounter={e} readOnly={closed} onIssued={refresh} />
      </>
    ),
    lab_order: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_lab_order')} />
        <LabOrderSection {...orderSection} />
      </>
    ),
    radiology_order: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_radiology_order')} />
        <RadiologyOrderSection {...orderSection} />
      </>
    ),
    procedure_order: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_procedure_order')} />
        <ProcedureOrderSection {...orderSection} />
      </>
    ),
    allergies: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_allergies')} />
        <AllergiesPanel encounter={e} readOnly={closed} />
      </>
    ),
    medicines: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_medicines')} />
        <CurrentMedicinesPanel patientId={e.patient.id} readOnly={closed} />
      </>
    ),
    problems: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_problems')} />
        <ProblemListPanel patientId={e.patient.id} readOnly={closed} />
      </>
    ),
    history: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_history')} />
        <PreviousVisits patientId={e.patient.id} excludeEncounterId={e.id} />
      </>
    ),
    files: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_files')} />
        <AttachmentsPanel encounterId={e.id} canWrite={can('encounter.write')} />
      </>
    ),
    certificates: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_certificates')} />
        <CertificatesPanel encounterId={e.id} />
      </>
    ),
    'care-team': () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_care_team')} />
        {e.appointmentId && <CareTeamPanel appointmentId={e.appointmentId} canWrite={!closed && can('encounter.write', 'appointment.manage')} />}
      </>
    ),
    addenda: () => (
      <>
        <SectionHeading section={section} description={tr('opd', 'emr_desc_addenda')} />
        <Addenda encounter={e} />
      </>
    ),
  };

  return (
    <AuthorisationProvider encounterId={e.id} enabled={Boolean(e.appointment?.payerId)}>
      <div ref={attachPage} className="[--emr-bar-top:-1.5rem] lg:[--emr-bar-top:-2.5rem]" style={stickyLayoutVars(barHeight, scrollAreaHeight)}>
        <EmrPatientBar
          ref={setBarNode}
          encounter={e}
          onRecordVitals={recordVitals}
          onOpenVitals={() => open('vitals')}
          progress={closed ? null : <EmrProgressIndicator progress={progress} onOpen={(key) => open(sectionOfFormKey(key))} />}
          actions={
            <>
              <MoreActions encounter={e} />
              {closed ? null : <CloseConsultationAction encounter={e} missing={progress.missing} onOpenForm={(key) => open(sectionOfFormKey(key))} />}
            </>
          }
        />

        <div className="mb-4 space-y-3 empty:hidden">
          <ConsultationFeeGateBanner encounter={e} />
          <AuthorisationBanner encounterId={e.id} />
          {related.length > 0 && <LinkChips links={related} />}
        </div>

        <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)] xl:grid-cols-[16rem_minmax(0,1fr)]">
          <aside className="min-w-0">
            <EmrNav sections={sections} active={active} onSelect={open} />
          </aside>
          <div key={active} className="min-w-0 space-y-4 animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
            {activeFormKey && isEmrNotNeededKey(activeFormKey) && requiredFormsApply(e) && (
              <NotNeededToggle encounter={e} formKey={activeFormKey} readOnly={closed || !can('encounter.write')} required={Boolean(activeForm?.required)} />
            )}
            {isClinicalFormCode(active) ? (
              <ClinicalFormSection scope={{ encounterId: e.id }} code={active} closed={closed} canWrite={can('encounter.write', 'vitals.write')} />
            ) : (
              body[active as EmrSectionId]?.()
            )}
          </div>
        </div>
      </div>

      {recordingVitals && (
        <VitalsDialog
          open
          onClose={() => setRecordingVitals(false)}
          patientId={e.patient.id}
          patientName={`${e.patient.fullName} · ${e.patient.mrn}`}
          encounterId={e.id}
          invalidateKeys={[['encounter', e.id]]}
          recheck={nextVitalsAreRecheck(e)}
          newborn={newbornFactsOf(e.patient)}
        />
      )}
    </AuthorisationProvider>
  );
}

function MoreActions({ encounter }: { encounter: Encounter }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const toast = useToast();
  const [requestingWaiver, setRequestingWaiver] = useState(false);
  const requestWaiver = async (reason: string) => {
    try {
      await post(`/appointments/${encounter.appointmentId}/fee-waiver-request`, { reason });
      toast.success(tr('opd', 'waiver_request_sent_to_the_cash_counter'));
      setRequestingWaiver(false);
    } catch (error) {
      toast.error(tr('opd', 'could_not_send_the_waiver_request'), describeError(error));
    }
  };
  const print = async (path: string) => {
    try {
      const doc = await get<{ html: string }>(path);
      printDocument(doc.html);
    } catch (error) {
      toast.error(tr('opd', 'emr_could_not_print'), describeError(error));
    }
  };
  return (
    <>
      <ActionMenu
        label={tr('opd', 'emr_more_actions')}
        trigger={
          <Button variant="outline" size="sm">
            <MoreHorizontal className="h-4 w-4" aria-hidden /> {tr('opd', 'emr_more_actions')}
          </Button>
        }
      >
        <ActionMenuItem icon={<Printer />} onSelect={() => void print(`/encounters/${encounter.id}/print`)}>
          {tr('opd', 'print_note')}
        </ActionMenuItem>
        <ActionMenuItem icon={<FileText />} onSelect={() => void print(`/encounters/${encounter.id}/print?slip=1`)}>
          {tr('opd', 'visit_summary')}
        </ActionMenuItem>
        <ActionMenuItem icon={<FolderOpen />} onSelect={() => navigate(`/patients/${encounter.patient.id}`)}>
          {tr('opd', 'full_record')}
        </ActionMenuItem>
        {can('invoice.read') && (
          <ActionMenuItem icon={<Receipt />} onSelect={() => navigate(`/billing/statement?encounterId=${encounter.id}`)}>
            {tr('opd', 'statement')}
          </ActionMenuItem>
        )}
        {encounter.appointmentId && can('encounter.write') && (
          <ActionMenuItem icon={<HandCoins />} onSelect={() => setRequestingWaiver(true)}>
            {tr('opd', 'request_fee_waiver')}
          </ActionMenuItem>
        )}
      </ActionMenu>
      {requestingWaiver && (
        <ReasonDialog
          title={tr('opd', 'request_fee_waiver')}
          description={tr('opd', 'emr_waiver_prompt')}
          minLength={10}
          maxLength={400}
          submitLabel={tr('opd', 'send_waiver_request')}
          onSubmit={requestWaiver}
          onClose={() => setRequestingWaiver(false)}
        />
      )}
    </>
  );
}

function CloseConsultationAction({ encounter, missing, onOpenForm }: { encounter: Encounter; missing: readonly string[]; onOpenForm: (key: string) => void }) {
  const { summary } = useAuthorisation();
  const model = bannerFor(summary);
  const hint = model?.state === 'needs_send' && model.note === 'automatic' ? tr('opd', 'closing_sends_for_authorisation') : null;
  return (
    <CloseConsultationButton
      encounter={encounter}
      hasDiagnosis={encounter.diagnoses.length > 0}
      hint={hint}
      feeGate={encounter.feeGate}
      missingRequired={missing}
      onOpenForm={onOpenForm}
    />
  );
}

function ConsultationFeeGateBanner({ encounter }: { encounter: Encounter }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);
  const banner = feeBannerFor(encounter.feeGate, encounter.status);
  if (!banner) return null;
  const checkAgain = async () => {
    setChecking(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });
    } finally {
      setChecking(false);
    }
  };
  return (
    <Alert variant="warning" title={tr('opd', 'consultation_fee_unpaid')}>
      {banner === 'block' ? tr('opd', 'fee_unpaid_collect_before_closing') : tr('opd', 'fee_unpaid_collect_at_counter')}
      {can('invoice.read') && (
        <>
          {' '}
          <Link to={`/billing/statement?encounterId=${encounter.id}`} className="font-medium underline underline-offset-2">
            {tr('opd', 'collect_at_counter')}
          </Link>
        </>
      )}
      <div className="mt-2">
        <Button size="sm" variant="outline" onClick={() => void checkAgain()} loading={checking}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'check_payment_again')}
        </Button>
      </div>
    </Alert>
  );
}
